import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  accountantApi,
  publicAccountantApi,
  type CreateAccountantPayload,
  type UpdateAccountantPayload,
} from './api';

export const accountantKeys = {
  all: ['accountants'] as const,
  list: () => ['accountants', 'list'] as const,
  detail: (id: string) => ['accountants', 'detail', id] as const,
  shares: (id: string) => ['accountants', 'shares', id] as const,
  publicView: (token: string, month: string) =>
    ['public-accountant', token, month] as const,
};

export function useAccountants() {
  return useQuery({
    queryKey: accountantKeys.list(),
    queryFn: () => accountantApi.list(),
  });
}

export function useAccountantShares(id: string | null) {
  return useQuery({
    queryKey: id ? accountantKeys.shares(id) : ['accountants', 'shares', 'noop'],
    queryFn: () => accountantApi.listShares(id!),
    enabled: !!id,
  });
}

export function useAccountantMutations() {
  const qc = useQueryClient();
  const invalidate = (id?: string): void => {
    qc.invalidateQueries({ queryKey: accountantKeys.list() });
    if (id) {
      qc.invalidateQueries({ queryKey: accountantKeys.detail(id) });
      qc.invalidateQueries({ queryKey: accountantKeys.shares(id) });
    }
  };

  const create = useMutation({
    mutationFn: (payload: CreateAccountantPayload) => accountantApi.create(payload),
    onSuccess: () => invalidate(),
  });

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateAccountantPayload }) =>
      accountantApi.update(id, payload),
    onSuccess: (_d, v) => invalidate(v.id),
  });

  const remove = useMutation({
    mutationFn: (id: string) => accountantApi.remove(id),
    onSuccess: () => invalidate(),
  });

  const generateLink = useMutation({
    mutationFn: (id: string) => accountantApi.generateLink(id),
    onSuccess: (_d, id) => invalidate(id),
  });

  const rotateToken = useMutation({
    mutationFn: (id: string) => accountantApi.rotateToken(id),
    onSuccess: (_d, id) => invalidate(id),
  });

  return { create, update, remove, generateLink, rotateToken };
}

export function usePublicAccountantView(token: string | null, month: string) {
  return useQuery({
    queryKey: token ? accountantKeys.publicView(token, month) : ['public-accountant', 'noop'],
    queryFn: () => publicAccountantApi.view(token!, month),
    enabled: !!token && !!month,
    retry: (failureCount, error) => {
      // 410 Gone (expirado) e 404 não devem ser retriados.
      const status = (error as { status?: number } | null)?.status;
      if (status === 410 || status === 404 || status === 403) return false;
      return failureCount < 2;
    },
  });
}
